import { apiAction, API_ERROR, API_SUCCESS } from "../dux/api";
import { apiCallComplete, apiCallStart } from "../dux/apiStatus";
import { openSnackbar } from "../dux/snackbar";
import {
  getPricingConfigsUrl,
  getPricingUrl,
  getUpdatePricingConfigUrl,
  getAllPricingConfigsUrl,
  getSpecialOffersUrl,
  getDeletePricingConfigUrl,
  getUpsertPricingUrl,
  getSimplePricingUrl,
} from "../helpers/apiEndPoints";

// Action types for pricing operations
export const GET_PRICING_CONFIGS = "GET_PRICING_CONFIGS";
export const GET_PRICING = "GET_PRICING";
export const UPDATE_PRICING_CONFIG = "UPDATE_PRICING_CONFIG";
export const GET_ALL_PRICING_CONFIGS = "GET_ALL_PRICING_CONFIGS";
export const GET_SPECIAL_OFFERS = "GET_SPECIAL_OFFERS";
export const DELETE_PRICING_CONFIG = "DELETE_PRICING_CONFIG";
export const UPSERT_PRICING = "UPSERT_PRICING";
export const GET_SIMPLE_PRICING = "GET_SIMPLE_PRICING";

const pricing = () => (next) => (action) => {
  next(action);

  // Get pricing configs
  if (action.type === GET_PRICING_CONFIGS) {
    next(apiCallStart({ api: "getPricingConfigs" }));
    next(
      apiAction({
        url: getPricingConfigsUrl(
          action.payload.sport,
          action.payload.location,
          action.payload.date
        ),
        feature: GET_PRICING_CONFIGS,
        method: "GET",
        data: action.payload.queryParams, // query parameters
      })
    );
  }

  if (action.type === `${GET_PRICING_CONFIGS} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "getPricingConfigs" }));
  }

  if (action.type === `${GET_PRICING_CONFIGS} ${API_ERROR}`) {
    next(apiCallComplete({ api: "getPricingConfigs" }));
    next(
      openSnackbar({
        message: "Failed to fetch pricing configs",
        type: "error",
      })
    );
  }

  // Get pricing for specific booking
  if (action.type === GET_PRICING) {
    next(apiCallStart({ api: "getPricing" }));
    next(
      apiAction({
        url: getPricingUrl(
          action.payload.sport,
          action.payload.location,
          action.payload.turfNumber
        ),
        feature: GET_PRICING,
        method: "GET",
        data: {
          date_string: action.payload.dateString,
          start_time: action.payload.startTime,
          end_time: action.payload.endTime,
        },
      })
    );
  }

  if (action.type === `${GET_PRICING} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "getPricing" }));
  }

  if (action.type === `${GET_PRICING} ${API_ERROR}`) {
    next(apiCallComplete({ api: "getPricing" }));
    next(openSnackbar({ message: "Failed to fetch pricing", type: "error" }));
  }

  // Update pricing config
  if (action.type === UPDATE_PRICING_CONFIG) {
    next(apiCallStart({ api: "updatePricingConfig" }));
    next(
      apiAction({
        url: getUpdatePricingConfigUrl(action.payload.id),
        feature: UPDATE_PRICING_CONFIG,
        method: "PUT",
        data: action.payload.data,
      })
    );
  }

  if (action.type === `${UPDATE_PRICING_CONFIG} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "updatePricingConfig" }));
    next(openSnackbar({ message: "Pricing config updated successfully" }));
  }

  if (action.type === `${UPDATE_PRICING_CONFIG} ${API_ERROR}`) {
    next(apiCallComplete({ api: "updatePricingConfig" }));
    next(
      openSnackbar({
        message: "Failed to update pricing config",
        type: "error",
      })
    );
  }

  // Get all pricing configs
  if (action.type === GET_ALL_PRICING_CONFIGS) {
    next(apiCallStart({ api: "getAllPricingConfigs" }));
    next(
      apiAction({
        url: getAllPricingConfigsUrl(),
        feature: GET_ALL_PRICING_CONFIGS,
        method: "GET",
        data: action.payload, // query parameters
      })
    );
  }

  if (action.type === `${GET_ALL_PRICING_CONFIGS} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "getAllPricingConfigs" }));
  }

  if (action.type === `${GET_ALL_PRICING_CONFIGS} ${API_ERROR}`) {
    next(apiCallComplete({ api: "getAllPricingConfigs" }));
    next(
      openSnackbar({
        message: "Failed to fetch all pricing configs",
        type: "error",
      })
    );
  }

  // Get special offers
  if (action.type === GET_SPECIAL_OFFERS) {
    next(apiCallStart({ api: "getSpecialOffers" }));
    next(
      apiAction({
        url: getSpecialOffersUrl(),
        feature: GET_SPECIAL_OFFERS,
        method: "GET",
        data: action.payload, // query parameters
      })
    );
  }

  if (action.type === `${GET_SPECIAL_OFFERS} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "getSpecialOffers" }));
  }

  if (action.type === `${GET_SPECIAL_OFFERS} ${API_ERROR}`) {
    next(apiCallComplete({ api: "getSpecialOffers" }));
    next(
      openSnackbar({ message: "Failed to fetch special offers", type: "error" })
    );
  }

  // Delete pricing config
  if (action.type === DELETE_PRICING_CONFIG) {
    next(apiCallStart({ api: "deletePricingConfig" }));
    next(
      apiAction({
        url: getDeletePricingConfigUrl(action.payload.id),
        feature: DELETE_PRICING_CONFIG,
        method: "DELETE",
      })
    );
  }

  if (action.type === `${DELETE_PRICING_CONFIG} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "deletePricingConfig" }));
    next(openSnackbar({ message: "Pricing config deleted successfully" }));
  }

  if (action.type === `${DELETE_PRICING_CONFIG} ${API_ERROR}`) {
    next(apiCallComplete({ api: "deletePricingConfig" }));
    next(
      openSnackbar({
        message: "Failed to delete pricing config",
        type: "error",
      })
    );
  }

  // Upsert pricing
  if (action.type === UPSERT_PRICING) {
    next(apiCallStart({ api: "upsertPricing" }));
    next(
      apiAction({
        url: getUpsertPricingUrl(),
        feature: UPSERT_PRICING,
        method: "POST",
        data: action.payload,
      })
    );
  }

  if (action.type === `${UPSERT_PRICING} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "upsertPricing" }));
    next(openSnackbar({ message: "Pricing configs upserted successfully" }));
  }

  if (action.type === `${UPSERT_PRICING} ${API_ERROR}`) {
    next(apiCallComplete({ api: "upsertPricing" }));
    next(
      openSnackbar({
        message: "Failed to upsert pricing configs",
        type: "error",
      })
    );
  }

  // Get simple pricing
  if (action.type === GET_SIMPLE_PRICING) {
    next(apiCallStart({ api: "getSimplePricing" }));
    next(
      apiAction({
        url: getSimplePricingUrl(action.payload.sport, action.payload.location),
        feature: GET_SIMPLE_PRICING,
        method: "GET",
      })
    );
  }

  if (action.type === `${GET_SIMPLE_PRICING} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "getSimplePricing" }));
  }

  if (action.type === `${GET_SIMPLE_PRICING} ${API_ERROR}`) {
    next(apiCallComplete({ api: "getSimplePricing" }));
    next(
      openSnackbar({ message: "Failed to fetch simple pricing", type: "error" })
    );
  }
};

export default pricing;
